import React from 'react';
import { Card, List, Tag, Progress, Button, Typography, Space, Empty } from 'antd';
import { CheckSquareOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import dayjs from 'dayjs';

const { Text } = Typography;

const MyTasksSummary = () => {
  const navigate = useNavigate();

  const { data: myTasksData, isLoading } = useQuery(
    'myTasksSummary',
    () => axios.get('/tasks/my', { 
      params: { status: 'pending,in_progress' } 
    }).then(res => res.data.data),
    { refetchInterval: 30000 }
  );

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'red',
      medium: 'orange',
      low: 'green'
    };
    return colors[priority] || 'default';
  };

  const getPriorityText = (priority) => {
    const texts = {
      high: '高',
      medium: '中',
      low: '低'
    };
    return texts[priority] || priority;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'default',
      in_progress: 'processing',
      completed: 'success',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: '待开始',
      in_progress: '进行中',
      completed: '已完成',
      cancelled: '已取消'
    };
    return texts[status] || status;
  };

  const isOverdue = (dueDate, status) => {
    return status !== 'completed' && dueDate && dayjs().isAfter(dayjs(dueDate));
  };

  // 展平任务数据
  const allTasks = myTasksData?.tasksByProject?.reduce((acc, group) => {
    return [...acc, ...group.tasks];
  }, []) || [];

  // 只显示前5个任务
  const displayTasks = allTasks.slice(0, 5);

  return (
    <Card 
      title={
        <Space>
          <CheckSquareOutlined />
          我的任务概览
        </Space>
      }
      extra={
        <Button 
          type="link" 
          onClick={() => navigate('/my-tasks')}
        >
          查看全部
        </Button>
      }
      className="status-card"
    >
      {displayTasks.length === 0 ? (
        <Empty 
          description="暂无待处理任务"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <List
          loading={isLoading}
          dataSource={displayTasks}
          renderItem={(task) => (
            <List.Item
              style={{ 
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '1px solid #f0f0f0'
              }}
              onClick={() => navigate(`/projects/${task.project.id}`)}
            >
              <List.Item.Meta
                title={
                  <Space>
                    <Text strong>{task.title}</Text>
                    <Tag color={getPriorityColor(task.priority)}>
                      {getPriorityText(task.priority)}
                    </Tag>
                    <Tag color={getStatusColor(task.status)}>
                      {getStatusText(task.status)}
                    </Tag>
                    {isOverdue(task.dueDate, task.status) && (
                      <Tag color="red">已逾期</Tag>
                    )}
                  </Space>
                }
                description={
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">
                      项目: {task.project.name}
                    </Text>
                    {task.dueDate && (
                      <Space>
                        <ClockCircleOutlined />
                        <Text type="secondary">
                          截止: {dayjs(task.dueDate).format('MM-DD HH:mm')}
                        </Text>
                      </Space>
                    )}
                    <div style={{ marginTop: '8px' }}>
                      <Progress 
                        percent={task.progress} 
                        size="small"
                        status={task.progress === 100 ? 'success' : 'active'}
                        showInfo={false}
                      />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {task.progress}% 完成
                      </Text>
                    </div>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Card>
  );
};

export default MyTasksSummary;
